import { type MedplumClient, formatHumanName } from '@medplum/core';
import type {
  Bundle,
  Practitioner,
  Provenance,
  Resource,
  Task,
} from '@medplum/fhirtypes';

import { withTaskCompletion } from '@eluve/clinical-tasks';
import {
  type FhirConnectionState,
  type FhirQueryState,
  useFhirConnection,
  useFhirData,
} from '@eluve/fhir-data';
import { flattenFhirGraphQLData } from '@eluve/fhir-renderer';
import { type ResultOf } from '@eluve/graphql-fhir';

import { FHIR_PATIENT_ID_QUERY, PATIENT_TASKS_QUERY } from './fhirQueries';

/**
 * Data layer for the My-tasks card: reads via the FHIR GraphQL connection,
 * the complete/undo flip via the Medplum SDK client under the clinician's
 * own identity. The two paths share no cache — callers refetch the read
 * after a write lands.
 */

function mapPatientId(
  data: ResultOf<typeof FHIR_PATIENT_ID_QUERY>,
): string | null {
  return data.PatientList?.[0]?.id ?? null;
}

/** Resolve the FHIR Patient id for an Eluve (Hasura) patient id. */
export function useFhirPatientId(
  patientId: string,
): FhirQueryState<string | null> {
  return useFhirData(FHIR_PATIENT_ID_QUERY, mapPatientId, {
    variables: { identifier: patientId },
  });
}

/**
 * Flatten the connection payload into Tasks, backfilling each owner
 * reference's `display` from the inline-resolved Practitioner (Clinical
 * Tasks writes owner references without one; the flatten helper strips the
 * nested resource into the collected list).
 */
export function mapTasks(data: ResultOf<typeof PATIENT_TASKS_QUERY>): Task[] {
  const resources = flattenFhirGraphQLData(data);
  const names = practitionerNamesByRef(resources);
  return resources
    .filter(
      (resource): resource is Task & { id: string } =>
        resource.resourceType === 'Task' && Boolean(resource.id),
    )
    .map((task) => {
      const ownerRef = task.owner?.reference;
      if (!task.owner || task.owner.display || !ownerRef) return task;
      const display = names.get(ownerRef);
      return display ? { ...task, owner: { ...task.owner, display } } : task;
    });
}

function practitionerNamesByRef(resources: Resource[]): Map<string, string> {
  const names = new Map<string, string>();
  for (const resource of resources) {
    if (resource.resourceType !== 'Practitioner' || !resource.id) continue;
    const name = (resource as Practitioner).name?.[0];
    if (name) names.set(`Practitioner/${resource.id}`, formatHumanName(name));
  }
  return names;
}

/**
 * The clinician's tasks for this patient, paged newest-first. Skipped until
 * both the FHIR patient id and the practitioner id resolve.
 */
export function usePatientTasks(
  fhirPatientId: string | null,
  practitionerId: string | null,
): FhirConnectionState<Task[]> {
  return useFhirConnection(PATIENT_TASKS_QUERY, 'TaskConnection', mapTasks, {
    variables: {
      patientRef: `Patient/${fhirPatientId}`,
      ownerRef: `Practitioner/${practitionerId}`,
    },
    skip: !fhirPatientId || !practitionerId,
  });
}

/**
 * Flip a task Done ⇄ To do — the same transition the Clinical Tasks panel
 * applies. Fresh-read-then-write so no fields are lost on the PUT. The
 * status change is a lifecycle decision, so it bundles an attestation
 * Provenance with the write (who completed/reopened it, and when — the
 * panel's `commitTaskWrite` semantics), falling back to a plain update when
 * the acting clinician isn't resolvable. Invalidates the SDK's ~60s search
 * cache so the panel's next read sees the flip; the caller refetches its
 * own GraphQL read.
 */
export async function setTaskDone(
  client: MedplumClient,
  args: {
    taskId: string;
    done: boolean;
    /** `Practitioner/<id>` recorded as the verifier on the Provenance. */
    practitionerReference: string | null;
  },
): Promise<void> {
  const { taskId, done, practitionerReference } = args;
  const current = await client.readResource('Task', taskId);
  const next = withTaskCompletion(current, done);
  if (practitionerReference) {
    const response = await client.executeBatch({
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        {
          request: { method: 'PUT', url: `Task/${taskId}` },
          resource: next,
        },
        {
          request: { method: 'POST', url: 'Provenance' },
          resource: buildAttestationProvenance(
            `Task/${taskId}`,
            practitionerReference,
          ),
        },
      ],
    });
    assertNoFailedEntries(response);
  } else {
    await client.updateResource(next);
  }
  client.invalidateSearches('Task');
}

function assertNoFailedEntries(response: Bundle): void {
  const statuses = (response.entry ?? [])
    .map((entry) => Number.parseInt(entry.response?.status ?? '', 10))
    .filter((code) => !(code >= 200 && code < 300));
  if (statuses.length === 0) return;
  throw new Error(`Task write failed: ${statuses.join(', ')}`);
}

const DATA_OPERATION_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-DataOperation';
const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/provenance-participant-type';

/** Attestation Provenance for a lifecycle change — the same shape the
 *  Clinical Tasks panel and backend review path write. */
function buildAttestationProvenance(
  targetReference: string,
  practitionerReference: string,
): Provenance {
  return {
    resourceType: 'Provenance',
    target: [{ reference: targetReference }],
    recorded: new Date().toISOString(),
    activity: {
      coding: [
        { system: DATA_OPERATION_SYSTEM, code: 'UPDATE', display: 'revise' },
      ],
    },
    agent: [
      {
        type: {
          coding: [
            { system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'verifier' },
          ],
        },
        who: { reference: practitionerReference },
      },
    ],
  };
}
